// STO-3295: Mobile and tablet cart sheets migrated from custom MobileSheet/TabletDimmer
// to MUI SwipeableDrawer. Swipe-to-dismiss, Escape handling, focus trap, and aria-modal
// are provided by SwipeableDrawer's internal Modal wrapper. Scroll lock is handled
// explicitly via position:fixed (iOS Safari ignores overflow:hidden on body).
import { useMemo } from 'react'
import { useScrollLock } from '@/hooks/useScrollLock'
import Box from '@/components/common/Box'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'

import { closeCartDrawer, toggleCartDrawer } from '@/store/uiSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectItemsRemovedCount, clearItemsRemovedCount } from '@/store/ticketSlice'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import { useLocationRef } from '@/contexts/RefContext'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { OrderDetailsPanel } from '@/components/cart/OrderDetailsPanel'
import { ItemsRemovedBanner } from '@/components/cart/ItemsRemovedBanner'
import {
	DesktopSidebar,
	TabletDimmer,
	TabletModal,
	TabletStickyBar,
	MobileStickyBar,
	drawerPaperSx,
	DrawerPuller,
} from './CartSidebar.styled'

interface CartSidebarProps {
	scheduleId: string
	timeZoneOffset?: number
	currency?: string
}

export default function CartSidebar({ scheduleId, timeZoneOffset, currency }: CartSidebarProps) {
	const isCartDrawerOpen = useAppSelector((state) => state.ui.isCartDrawerOpen)
	const itemsRemovedCount = useAppSelector(selectItemsRemovedCount)
	const dispatch = useAppDispatch()
	const locationRef = useLocationRef()
	const { t } = useFormatMessage()

	const { data: itemsData } = useMenuItems(locationRef, scheduleId)
	const menuReady = itemsData !== undefined
	const menuItems = useMemo(() => {
		const map: Record<string, NonNullable<typeof itemsData>['items'][number]> = {}
		if (itemsData) {
			for (const item of itemsData.items) {
				map[item.ref] = item
			}
		}
		return map
	}, [itemsData])

	useScrollLock(isCartDrawerOpen)

	const banner =
		itemsRemovedCount > 0 ? (
			<ItemsRemovedBanner
				count={itemsRemovedCount}
				onDismiss={() => dispatch(clearItemsRemovedCount())}
			/>
		) : null

	return (
		<>
			{/* Desktop — fills the sticky wrapper height; items list scrolls inside */}
			<DesktopSidebar
				data-testid="cart-sidebar-desktop"
				sx={{ display: { xs: 'none', lg: 'flex' } }}
			>
				{banner}
				{menuReady && (
					<OrderDetailsPanel
						menuItems={menuItems}
						timeZoneOffset={timeZoneOffset}
						currency={currency}
					/>
				)}
			</DesktopSidebar>

			{/* Tablet — CartBottomBar handles md viewport; this region is hidden to avoid duplication (STO-3279) */}
			<Box data-testid="cart-sidebar-tablet" sx={{ display: 'none' }}>
				{/* Dimmer — always in DOM; visible only when drawer is open */}
				<TabletDimmer
					role="dialog"
					aria-modal="true"
					aria-hidden={!isCartDrawerOpen}
					aria-label={t('component:orderDetailsPanel:heading:label')}
					data-testid="cart-dimmer"
					isOpen={isCartDrawerOpen}
					onClick={() => dispatch(closeCartDrawer())}
				>
					<TabletModal onClick={(e: React.MouseEvent) => e.stopPropagation()}>
						{banner}
						{menuReady && (
							<OrderDetailsPanel
								menuItems={menuItems}
								timeZoneOffset={timeZoneOffset}
								currency={currency}
							/>
						)}
					</TabletModal>
				</TabletDimmer>

				{/* Bottom sticky bar — always in DOM; visible only when drawer is closed */}
				<TabletStickyBar
					data-testid="cart-sticky-bar-tablet"
					isOpen={isCartDrawerOpen}
					aria-label={t('page:home:reviewOrder:text')}
					aria-haspopup="dialog"
					onClick={() => dispatch(toggleCartDrawer())}
				>
					{t('page:home:reviewOrder:text')}
				</TabletStickyBar>
			</Box>

			{/* Mobile — SwipeableDrawer provides swipe-to-dismiss, scroll lock, Escape handling,
			    focus trap, and aria-modal. CartBottomBar handles xs/sm viewport trigger; this
			    region is hidden to avoid duplication (STO-3279). */}
			<Box data-testid="cart-sidebar-mobile" sx={{ display: 'none' }}>
				<SwipeableDrawer
					anchor="bottom"
					open={isCartDrawerOpen}
					onOpen={() => {
						dispatch(toggleCartDrawer())
					}}
					onClose={() => {
						dispatch(closeCartDrawer())
					}}
					aria-label={t('component:orderDetailsPanel:heading:label')}
					slotProps={{ paper: { sx: drawerPaperSx } }}
					disableSwipeToOpen
				>
					<DrawerPuller aria-hidden="true" />
					{banner}
					{menuReady && (
						<OrderDetailsPanel
							menuItems={menuItems}
							timeZoneOffset={timeZoneOffset}
							currency={currency}
						/>
					)}
				</SwipeableDrawer>

				{/* Sticky bar — always in DOM; visible only when drawer is closed */}
				<MobileStickyBar
					data-testid="cart-sticky-bar-mobile"
					aria-label={t('page:home:reviewOrder:text')}
					aria-haspopup="dialog"
					onClick={() => dispatch(toggleCartDrawer())}
				>
					{t('page:home:reviewOrder:text')}
				</MobileStickyBar>
			</Box>
		</>
	)
}
